import React, { useState, useEffect, useMemo, Component } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { apiClient } from '@/services/api'
import {
  ArrowLeft, Users, Trophy, Clock, Target, ChevronDown, ChevronUp,
  CheckCircle, XCircle, BarChart2, AlertCircle, TrendingUp, Award, Minus,
  User, Activity as ActivityIcon, Download, FileSpreadsheet, FileText, Info,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import type { Lesson, LessonActivity, Player, DetailedPlayerAnalytics, LessonOverview } from '@/types'
import { generateExcelReport, generatePdfReport, getCurrentPdfTheme, type ReportData } from '@/utils/reportGenerator'

// Error boundary to prevent render crashes from blanking the entire app
class LessonReportErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[LessonReport] Render crash:', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-theme-bg text-theme-text flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
            <p className="text-red-400 mb-2">Something went wrong loading this report.</p>
            <p className="text-theme-text-muted text-sm mb-4">{this.state.error?.message}</p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-surface-hover rounded-lg text-theme-text hover:bg-surface-hover transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// Backend response fields extend the frontend PlayerResponse type
interface RawResponse {
  id: string
  player_id: string
  activity_id: string
  response_data?: any    // actual backend jsonb field
  content?: any          // serializer alias
  score?: number
  feedback?: string
  submitted_at: string
}

const ACTIVITY_LABELS: Record<string, string> = {
  mcq: 'MCQ',
  coding: 'Coding',
  command_shell: 'Shell',
  phishing_quiz: 'Phishing Quiz',
  packet_analysis: 'Packet Analysis',
  pdf_slideshow: 'Slide',
  google_slides: 'Google Slides',
  cyber_hygiene_quiz: 'Cyber Hygiene Quiz',
  coding_quiz: 'Coding Quiz',
  python_repl: 'Python Coding',
  youtube_video: 'YouTube Video',
}

const SLIDE_TYPES = ['pdf_slideshow', 'google_slides']
const isRespondable = (a: LessonActivity) => !SLIDE_TYPES.includes(a.activity_type)

// Helpers

function getContent(r: RawResponse): any {
  return r.response_data ?? r.content
}

function formatAnswer(activity: LessonActivity, r: RawResponse): string {
  const data = getContent(r)
  if (data === null || data === undefined) return '\u2014'
  switch (activity.activity_type) {
    case 'mcq': {
      const idx = data?.answer ?? data
      const options: string[] = activity.activity_data?.options ?? []
      if (typeof idx === 'number' && options[idx] !== undefined) {
        return `${String.fromCharCode(65 + idx)}. ${options[idx]}`
      }
      return String(idx)
    }
    case 'phishing_quiz': {
      const answers = data?.answers
      if (Array.isArray(answers) && answers.length > 0) {
        return answers[0].correct ? 'Legitimate' : 'Phishing'
      }
      return '\u2014'
    }
    case 'packet_analysis':
    case 'cyber_hygiene_quiz':
    case 'coding_quiz': {
      const answers = data?.answers
      if (Array.isArray(answers) && answers.length > 0) {
        return answers[0].option_text ?? String(answers[0].choice ?? '\u2014')
      }
      return '\u2014'
    }
    case 'command_shell':
      return data?.answer ? String(data.answer) : 'Shell response submitted'
    case 'coding':
      return data?.code ? `Code submitted (${String(data.code).length} chars)` : 'Code submitted'
    default:
      if (typeof data === 'object' && data !== null) {
        return String(data?.answer ?? data?.word ?? data?.value ?? JSON.stringify(data))
      }
      return String(data)
  }
}

function isCorrect(activity: LessonActivity, r: RawResponse): boolean | null {
  const data = getContent(r)
  if (!data) return null

  switch (activity.activity_type) {
    case 'mcq': {
      const correct = activity.activity_data?.correct_answer
      const chosen = data?.answer
      if (correct === undefined || chosen === undefined) return null
      return Number(chosen) === Number(correct)
    }
    case 'phishing_quiz':
    case 'packet_analysis':
    case 'cyber_hygiene_quiz':
    case 'coding_quiz': {
      const answers = data?.answers
      if (Array.isArray(answers) && answers.length > 0) {
        return answers[0].correct === true
      }
      return null
    }
    case 'command_shell': {
      const expected = activity.activity_data?.correct_answer
      if (!expected || !data?.answer) return null
      return String(data.answer).trim().toLowerCase() === String(expected).trim().toLowerCase()
    }
    case 'python_repl': {
      const totalTests = data?.total_tests ?? 0
      const totalPassed = data?.total_passed ?? 0
      if (totalTests === 0) return null
      return totalPassed === totalTests
    }
    case 'coding': {
      const execution = data?.execution_result
      if (!execution?.test_results) return null
      const results = execution.test_results
      return results.length > 0 && results.every((t: any) => t.passed)
    }
    default:
      return null
  }
}

function formatDuration(lesson: Lesson): string {
  if (!lesson.started_at || !lesson.ended_at) return '\u2014'
  const secs = Math.floor(
    (new Date(lesson.ended_at).getTime() - new Date(lesson.started_at).getTime()) / 1000,
  )
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

function rankBadge(rank: number) {
  if (rank === 1)
    return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-yellow-500 text-yellow-900 font-bold text-sm">1</span>
  if (rank === 2)
    return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-400 text-gray-900 font-bold text-sm">2</span>
  if (rank === 3)
    return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-700 text-amber-100 font-bold text-sm">3</span>
  return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-surface-hover text-theme-text font-semibold text-sm">{rank}</span>
}

// Sub-components

interface LeaderboardEntry {
  id: string
  display_name: string
  totalScore: number
  activitiesResponded: number
  sessionDuration: number
}

const TOOLTIP_STYLE = {
  backgroundColor: 'var(--color-surface-deep)',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  color: 'var(--color-text)',
}

function AnalyticsOverviewTab({ lessonOverview }: { lessonOverview: LessonOverview | null }) {
  if (!lessonOverview) {
    return (
      <div className="text-center py-16 text-theme-text-muted">
        <BarChart2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p>Analytics data not available</p>
      </div>
    )
  }

  const { basicStats, engagement, activityPerformance } = lessonOverview

  const chartData = activityPerformance.map(a => ({
    name: a.title.length > 18 ? a.title.slice(0, 18) + '...' : a.title,
    responseRate: a.responseRate,
    avgTime: a.averageResponseTime,
    difficulty: a.difficultyScore,
  }))

  return (
    <div className="space-y-6">
      {/* Basic Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="bg-surface-deep rounded-xl p-4 border border-theme-border">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2 bg-blue-900/30 text-blue-400">
            <Users className="w-4 h-4" />
          </div>
          <div className="text-2xl font-bold">{basicStats.playerCount}</div>
          <div className="text-xs text-theme-text-muted mt-0.5">Students</div>
        </div>
        <div className="bg-surface-deep rounded-xl p-4 border border-theme-border">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2 bg-green-900/30 text-green-400">
            <ActivityIcon className="w-4 h-4" />
          </div>
          <div className="text-2xl font-bold">{basicStats.activityCount}</div>
          <div className="text-xs text-theme-text-muted mt-0.5">Activities</div>
        </div>
        <div className="bg-surface-deep rounded-xl p-4 border border-theme-border">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2 bg-yellow-900/30 text-yellow-400">
            <Clock className="w-4 h-4" />
          </div>
          <div className="text-2xl font-bold">{basicStats.duration}m</div>
          <div className="text-xs text-theme-text-muted mt-0.5">Duration</div>
        </div>
      </div>

      {/* Activity Performance Chart */}
      {chartData.length > 0 && (
        <div className="bg-surface-deep rounded-xl p-6 border border-theme-border">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-sm font-semibold text-[var(--color-primary)] uppercase tracking-wide">Activity Performance</h3>
            <div className="relative group">
              <Info className="w-4 h-4 text-theme-text-muted cursor-help" />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-3 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-lg text-xs text-[var(--color-text-muted)] opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50">
                <p className="font-semibold text-[var(--color-text)] mb-1">Response Rate %</p>
                <p>The percentage of students who submitted a response for each activity, calculated as (responses received / total students) × 100.</p>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="responseRate" name="Response Rate %" fill="#60A5FA" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Engagement Metrics */}
      <div className="bg-surface-deep rounded-xl p-6 border border-theme-border">
        <h3 className="text-sm font-semibold text-[var(--color-primary)] uppercase tracking-wide mb-4">Engagement Metrics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">{engagement.responseRate}%</div>
            <div className="text-xs text-theme-text-muted mt-1">Response Rate</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-400">{engagement.playerRetention}%</div>
            <div className="text-xs text-theme-text-muted mt-1">Student Retention</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-400">{engagement.averageSessionDuration}m</div>
            <div className="text-xs text-theme-text-muted mt-1">Avg Duration</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-400">{engagement.activityCompletionRate}%</div>
            <div className="text-xs text-theme-text-muted mt-1">Activity Completion</div>
          </div>
        </div>
      </div>
    </div>
  )
}

function LeaderboardTab({
  leaderboard,
  totalActivities,
}: {
  leaderboard: LeaderboardEntry[]
  totalActivities: number
}) {
  const maxScore = leaderboard.length > 0 ? leaderboard[0].totalScore : 1

  if (leaderboard.length === 0) {
    return (
      <div className="text-center py-16 text-theme-muted">
        <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p>No students in this lesson</p>
      </div>
    )
  }

  return (
    <div className="bg-surface-deep rounded-lg overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-theme-border text-left text-xs text-theme-text-muted uppercase tracking-wide">
            <th className="px-4 py-3 w-12">Rank</th>
            <th className="px-4 py-3">Student</th>
            <th className="px-4 py-3 w-48 hidden md:table-cell">Score</th>
            <th className="px-4 py-3 text-right w-24">Points</th>
            <th className="px-4 py-3 text-right w-28 hidden sm:table-cell">Responded</th>
            <th className="px-4 py-3 text-right w-28 hidden lg:table-cell">Session</th>
          </tr>
        </thead>
        <tbody>
          {leaderboard.map((player, i) => {
            const pct = maxScore > 0 ? (player.totalScore / maxScore) * 100 : 0
            const participation =
              totalActivities > 0
                ? Math.round((player.activitiesResponded / totalActivities) * 100)
                : 0
            return (
              <motion.tr
                key={player.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.03 }}
                className="border-b border-theme-border hover:bg-surface-hover transition-colors"
              >
                <td className="px-4 py-3">{rankBadge(i + 1)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center text-sm font-semibold text-theme-text shrink-0">
                      {(player.display_name || '?').charAt(0).toUpperCase()}
                    </div>
                    <span className="font-medium text-theme-text truncate max-w-[160px]">
                      {player.display_name}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-surface-hover rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-xs text-theme-text-muted w-8 text-right">{Math.round(pct)}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-bold text-yellow-400">
                  {player.totalScore}
                </td>
                <td className="px-4 py-3 text-right text-theme-text-muted hidden sm:table-cell text-sm">
                  {player.activitiesResponded}/{totalActivities}
                  <span className="text-theme-muted ml-1">({participation}%)</span>
                </td>
                <td className="px-4 py-3 text-right text-theme-text-muted hidden lg:table-cell text-sm">
                  {player.sessionDuration > 0 ? `${player.sessionDuration}m` : '\u2014'}
                </td>
              </motion.tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// MCQ option distribution bar
function McqDistribution({
  activity,
  responses,
}: {
  activity: LessonActivity
  responses: RawResponse[]
}) {
  const options: string[] = activity.activity_data?.options ?? []
  const correctIdx: number | undefined = activity.activity_data?.correct_answer
  if (options.length === 0) return null

  const counts = options.map((_, idx) =>
    responses.filter(r => {
      const ans = getContent(r)?.answer
      return Number(ans) === idx
    }).length,
  )
  const total = responses.length || 1

  return (
    <div className="space-y-2">
      {options.map((option, idx) => {
        const count = counts[idx]
        const pct = Math.round((count / total) * 100)
        const isCorrectOpt = idx === Number(correctIdx)
        return (
          <div key={idx} className="flex items-center gap-3">
            <span
              className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold shrink-0 ${
                isCorrectOpt
                  ? 'bg-green-700 text-green-100'
                  : 'bg-surface-hover text-theme-text'
              }`}
            >
              {String.fromCharCode(65 + idx)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm text-theme-text truncate">{option}</span>
                {isCorrectOpt && (
                  <span className="text-xs text-green-400 shrink-0">(correct)</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-surface-hover rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      isCorrectOpt ? 'bg-green-500' : 'bg-blue-600'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs text-theme-text-muted w-16 text-right">
                  {count} ({pct}%)
                </span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Correct/incorrect distribution for activities with a single right answer (e.g. command_shell)
function CorrectIncorrectDistribution({ activity, responses }: { activity: LessonActivity; responses: RawResponse[] }) {
  const total = responses.length || 1
  let correctCount = 0
  let incorrectCount = 0

  for (const r of responses) {
    const result = isCorrect(activity, r)
    if (result === true) correctCount++
    else if (result === false) incorrectCount++
  }

  return (
    <div className="space-y-2">
      {[
        { label: 'Correct', count: correctCount, color: 'bg-green-500', textColor: 'text-green-400' },
        { label: 'Incorrect', count: incorrectCount, color: 'bg-red-500', textColor: 'text-red-400' },
      ].map(({ label, count, color, textColor }) => {
        const pct = Math.round((count / total) * 100)
        return (
          <div key={label} className="flex items-center gap-3">
            <span className={`w-16 text-sm font-medium shrink-0 ${textColor}`}>{label}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-surface-hover rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${color}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs text-theme-text-muted w-16 text-right">
                  {count} ({pct}%)
                </span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Test case distribution for python_repl and coding/blockly activities
function TestCaseDistribution({ activity, responses }: { activity: LessonActivity; responses: RawResponse[] }) {
  const isPython = activity.activity_type === 'python_repl'

  const buckets: Record<string, number> = {}
  let maxTests = 0

  for (const r of responses) {
    const d = getContent(r)
    let passed: number
    let total: number

    if (isPython) {
      passed = d?.total_passed ?? 0
      total = d?.total_tests ?? 0
    } else {
      const results = d?.execution_result?.test_results
      if (!Array.isArray(results)) continue
      passed = results.filter((t: any) => t.passed).length
      total = results.length
    }

    if (total === 0) continue
    if (total > maxTests) maxTests = total
    const key = `${passed}/${total}`
    buckets[key] = (buckets[key] || 0) + 1
  }

  if (maxTests === 0) return null

  // Build sorted entries from 0/N to N/N
  const entries = Array.from({ length: maxTests + 1 }, (_, i) => {
    const key = `${i}/${maxTests}`
    return { passed: i, total: maxTests, count: buckets[key] || 0 }
  })

  const totalResponses = responses.length || 1

  return (
    <div className="space-y-2">
      {entries.map(({ passed, total, count }) => {
        const pct = Math.round((count / totalResponses) * 100)
        const isAllPassed = passed === total
        return (
          <div key={passed} className="flex items-center gap-3">
            <span
              className={`w-10 text-xs font-mono font-bold text-right shrink-0 ${
                isAllPassed ? 'text-green-400' : count > 0 ? 'text-theme-text' : 'text-theme-text-muted'
              }`}
            >
              {passed}/{total}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-surface-hover rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      isAllPassed ? 'bg-green-500' : 'bg-blue-600'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs text-theme-text-muted w-16 text-right">
                  {count} ({pct}%)
                </span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Answer distribution for quiz types that store answers as { answers: [{ choice: idx }] } with options/correct_answer in activity_data
function QuizOptionDistribution({
  activity,
  responses,
}: {
  activity: LessonActivity
  responses: RawResponse[]
}) {
  const options: string[] = activity.activity_data?.options ?? []
  const correctIdx: number | undefined = activity.activity_data?.correct_answer
  if (options.length === 0) return null

  const counts = options.map((_, idx) =>
    responses.filter(r => {
      const d = getContent(r)
      const firstAnswer = d?.answers?.[0]
      const choice = firstAnswer?.choice ?? firstAnswer?.answer
      return Number(choice) === idx
    }).length,
  )
  const total = responses.length || 1

  return (
    <div className="space-y-2">
      {options.map((option, idx) => {
        const count = counts[idx]
        const pct = Math.round((count / total) * 100)
        const isCorrectOpt = idx === Number(correctIdx)
        return (
          <div key={idx} className="flex items-center gap-3">
            <span
              className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold shrink-0 ${
                isCorrectOpt
                  ? 'bg-green-700 text-green-100'
                  : 'bg-surface-hover text-theme-text'
              }`}
            >
              {String.fromCharCode(65 + idx)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm text-theme-text truncate">{option}</span>
                {isCorrectOpt && (
                  <span className="text-xs text-green-400 shrink-0">(correct)</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-surface-hover rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      isCorrectOpt ? 'bg-green-500' : 'bg-blue-600'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs text-theme-text-muted w-16 text-right">
                  {count} ({pct}%)
                </span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Binary distribution for phishing quiz (phishing vs legitimate)
function PhishingAnswerDistribution({ activity, responses }: { activity: LessonActivity; responses: RawResponse[] }) {
  const total = responses.length || 1
  const correctAnswer: string | undefined = activity.activity_data?.type
  const choices = responses.map(r => {
    const d = getContent(r)
    const firstAnswer = d?.answers?.[0]
    return firstAnswer?.choice ?? firstAnswer?.answer ?? d?.choice ?? d?.answer
  })
  const phishingCount = choices.filter(c => c === 'phishing').length
  const legitimateCount = choices.filter(c => c === 'legitimate').length

  return (
    <div className="space-y-2">
      {[
        { label: 'Phishing', value: 'phishing', count: phishingCount },
        { label: 'Legitimate', value: 'legitimate', count: legitimateCount },
      ].map(({ label, value, count }) => {
        const pct = Math.round((count / total) * 100)
        const isCorrectOpt = value === correctAnswer
        return (
          <div key={label} className="flex items-center gap-3">
            <span
              className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold shrink-0 ${
                isCorrectOpt
                  ? 'bg-green-700 text-green-100'
                  : 'bg-surface-hover text-theme-text'
              }`}
            >
              {label.charAt(0)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm text-theme-text">{label}</span>
                {isCorrectOpt && (
                  <span className="text-xs text-green-400 shrink-0">(correct)</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-surface-hover rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      isCorrectOpt ? 'bg-green-500' : 'bg-blue-600'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs text-theme-text-muted w-16 text-right">
                  {count} ({pct}%)
                </span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function PhishingQuizDistribution({ responses, title = 'Phishing Quiz Performance' }: { responses: RawResponse[]; title?: string }) {
  // Aggregate category_breakdown across all student responses
  const aggregated: Record<string, { correct: number; total: number }> = {}
  let overallCorrect = 0
  let overallTotal = 0

  for (const r of responses) {
    const d = getContent(r)
    overallCorrect += d?.total_correct ?? 0
    overallTotal += d?.total_questions ?? 0

    const cb = d?.category_breakdown
    if (cb && typeof cb === 'object') {
      for (const [cat, val] of Object.entries(cb as Record<string, { correct: number; total: number }>)) {
        if (!aggregated[cat]) aggregated[cat] = { correct: 0, total: 0 }
        aggregated[cat].correct += val.correct ?? 0
        aggregated[cat].total += val.total ?? 0
      }
    }
  }

  const overallPct = overallTotal > 0 ? Math.round((overallCorrect / overallTotal) * 100) : 0
  const categories = Object.entries(aggregated)
    .map(([cat, v]) => ({
      category: cat,
      correct: v.correct,
      total: v.total,
      pct: v.total > 0 ? Math.round((v.correct / v.total) * 100) : 0,
    }))
    .sort((a, b) => a.pct - b.pct) // worst first

  const barColor = (pct: number) =>
    pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-yellow-500' : 'bg-red-500'

  return (
    <div className="space-y-3">
      <p className="text-xs text-theme-text-muted uppercase tracking-wide">
        {title}
      </p>

      {/* Overall accuracy */}
      <div className="flex items-center gap-3 p-3 bg-surface rounded-lg">
        <Target className="w-5 h-5 text-blue-400 shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-theme-text">Overall Accuracy</div>
          <div className="text-xs text-theme-text-muted">{overallCorrect}/{overallTotal} correct across {responses.length} students</div>
        </div>
        <div className="text-2xl font-bold text-blue-400">{overallPct}%</div>
      </div>

      {/* Per-category bars */}
      {categories.length > 0 ? (
        <div className="space-y-2">
          {categories.map(c => (
            <div key={c.category} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-theme-text">{c.category}</span>
                <span className="text-theme-text-muted">{c.correct}/{c.total} ({c.pct}%)</span>
              </div>
              <div className="h-2.5 bg-surface-hover rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${barColor(c.pct)}`}
                  style={{ width: `${c.pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-theme-muted italic">Category breakdown not available for these responses.</p>
      )}
    </div>
  )
}

function ActivitiesTab({
  activities,
  activityResponses,
  players,
  expandedActivityId,
  onToggleActivity,
}: {
  activities: LessonActivity[]
  activityResponses: Record<string, RawResponse[]>
  players: Player[]
  expandedActivityId: string | null
  onToggleActivity: (id: string) => void
}) {
  const playerMap = useMemo(() => {
    const m: Record<string, Player> = {}
    players.forEach(p => { m[p.id] = p })
    return m
  }, [players])

  if (activities.length === 0) {
    return (
      <div className="text-center py-16 text-theme-muted">
        <ActivityIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p>No activities in this lesson</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {activities.map(activity => {
        const responses = activityResponses[activity.id] ?? []
        const responseRate =
          players.length > 0 ? Math.round((responses.length / players.length) * 100) : 0
        const isExpanded = expandedActivityId === activity.id
        const isMcq = activity.activity_type === 'mcq'
        const isPhishing = activity.activity_type === 'phishing_quiz'
        const isPacketAnalysis = activity.activity_type === 'packet_analysis'
        const isCyberHygiene = activity.activity_type === 'cyber_hygiene_quiz'
        const isCodingQuiz = activity.activity_type === 'coding_quiz'
        const hasOptionDistribution = isCyberHygiene || isCodingQuiz || isPacketAnalysis
        const hasTestCaseDistribution = activity.activity_type === 'python_repl' || activity.activity_type === 'coding'
        const isCommandShell = activity.activity_type === 'command_shell'

        // Avg score from responses
        const scoredResponses = responses.filter(r => r.score !== undefined)
        const avgScore =
          scoredResponses.length > 0
            ? Math.round(
                scoredResponses.reduce((acc, r) => acc + Number(r.score ?? 0), 0) /
                  scoredResponses.length,
              )
            : null

        // MCQ correctness rate
        const correctCount = isMcq
          ? responses.filter(r => isCorrect(activity, r) === true).length
          : 0

        // Quiz accuracy (phishing, packet analysis, cyber hygiene, coding quiz)
        const quizAccuracy = (isPhishing || isPacketAnalysis || isCyberHygiene || isCodingQuiz) && responses.length > 0
          ? (() => {
              let totalCorrect = 0
              let totalQs = 0
              for (const r of responses) {
                const d = getContent(r)
                totalCorrect += d?.total_correct ?? 0
                totalQs += d?.total_questions ?? 0
              }
              return totalQs > 0 ? Math.round((totalCorrect / totalQs) * 100) : null
            })()
          : null

        return (
          <div key={activity.id} className="bg-surface-deep rounded-lg overflow-hidden">
            {/* Activity header */}
            <button
              onClick={() => onToggleActivity(activity.id)}
              className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-surface-hover transition-colors"
            >
              <span className="w-7 h-7 rounded-full bg-surface-hover text-theme-text-muted flex items-center justify-center text-xs font-mono shrink-0">
                {activity.sequence_number}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-theme-text truncate">
                    {activity.title}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs bg-surface-hover text-theme-text-muted shrink-0">
                    {ACTIVITY_LABELS[activity.activity_type] ?? activity.activity_type}
                  </span>
                  {activity.status === 'completed' && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-green-900/50 text-green-400 shrink-0">
                      completed
                    </span>
                  )}
                </div>
              </div>
              {/* Quick stats */}
              <div className="flex items-center gap-4 shrink-0 text-sm">
                <div className="text-right">
                  <div className="text-theme-text font-semibold">{responses.length}</div>
                  <div className="text-theme-muted text-xs">responses</div>
                </div>
                <div className="text-right hidden sm:block">
                  <div className="text-theme-text font-semibold">{responseRate}%</div>
                  <div className="text-theme-muted text-xs">rate</div>
                </div>
                {isMcq && responses.length > 0 && (
                  <div className="text-right hidden md:block">
                    <div className="text-green-400 font-semibold">
                      {Math.round((correctCount / responses.length) * 100)}%
                    </div>
                    <div className="text-theme-muted text-xs">correct</div>
                  </div>
                )}
                {quizAccuracy !== null && (
                  <div className="text-right hidden md:block">
                    <div className="text-green-400 font-semibold">{quizAccuracy}%</div>
                    <div className="text-theme-muted text-xs">correct</div>
                  </div>
                )}
                {avgScore !== null && (
                  <div className="text-right hidden lg:block">
                    <div className="text-yellow-400 font-semibold">{avgScore}</div>
                    <div className="text-theme-muted text-xs">avg pts</div>
                  </div>
                )}
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-theme-muted" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-theme-muted" />
                )}
              </div>
            </button>

            {/* Expanded detail */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4 border-t border-theme-border pt-4 space-y-4">
                    {responses.length === 0 ? (
                      <p className="text-theme-muted text-sm text-center py-4">
                        No responses recorded for this activity
                      </p>
                    ) : (
                      <>
                        {/* MCQ distribution */}
                        {isMcq && (
                          <div>
                            <p className="text-xs text-theme-text-muted uppercase tracking-wide mb-3">
                              Answer Distribution
                            </p>
                            <McqDistribution activity={activity} responses={responses} />
                          </div>
                        )}

                        {/* Option distribution for quiz types with options/correct_answer */}
                        {hasOptionDistribution && (
                          <div>
                            <p className="text-xs text-theme-text-muted uppercase tracking-wide mb-3">
                              Answer Distribution
                            </p>
                            <QuizOptionDistribution activity={activity} responses={responses} />
                          </div>
                        )}

                        {/* Phishing binary distribution */}
                        {isPhishing && (
                          <div>
                            <p className="text-xs text-theme-text-muted uppercase tracking-wide mb-3">
                              Answer Distribution
                            </p>
                            <PhishingAnswerDistribution activity={activity} responses={responses} />
                          </div>
                        )}

                        {/* Test case distribution for python_repl and coding/blockly */}
                        {hasTestCaseDistribution && (
                          <div>
                            <p className="text-xs text-theme-text-muted uppercase tracking-wide mb-3">
                              Test Cases Passed
                            </p>
                            <TestCaseDistribution activity={activity} responses={responses} />
                          </div>
                        )}

                        {/* Correct/incorrect distribution for command shell */}
                        {isCommandShell && (
                          <div>
                            <p className="text-xs text-theme-text-muted uppercase tracking-wide mb-3">
                              Answer Distribution
                            </p>
                            <CorrectIncorrectDistribution activity={activity} responses={responses} />
                          </div>
                        )}

                        {/* Per-player response table */}
                        <div>
                          <p className="text-xs text-theme-text-muted uppercase tracking-wide mb-3">
                            Individual Responses
                          </p>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-xs text-theme-muted border-b border-theme-border">
                                  <th className="pb-2 pr-4">Student</th>
                                  <th className="pb-2 pr-4">Response</th>
                                  <th className="pb-2 pr-4 text-center">Result</th>
                                  <th className="pb-2 pr-4 text-right">Score</th>
                                  <th className="pb-2 text-right">Time</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-theme-border">
                                {responses.map(r => {
                                  const player = playerMap[r.player_id]
                                  const correct = isCorrect(activity, r)
                                  const submittedAt = new Date(r.submitted_at)
                                  const startedAt = activity.started_at
                                    ? new Date(activity.started_at)
                                    : null
                                  const elapsed = startedAt
                                    ? Math.floor(
                                        (submittedAt.getTime() - startedAt.getTime()) / 1000,
                                      )
                                    : null

                                  return (
                                    <tr key={r.id} className="hover:bg-surface-hover">
                                      <td className="py-2 pr-4">
                                        <span className="text-theme-text font-medium">
                                          {player?.display_name ?? r.player_id.slice(0, 8)}
                                        </span>
                                      </td>
                                      <td className="py-2 pr-4 text-theme-text max-w-[200px] truncate">
                                        {formatAnswer(activity, r)}
                                      </td>
                                      <td className="py-2 pr-4 text-center">
                                        {correct === true ? (
                                          <CheckCircle className="w-4 h-4 text-[var(--color-success)] mx-auto" />
                                        ) : correct === false ? (
                                          <XCircle className="w-4 h-4 text-[var(--color-danger)] mx-auto" />
                                        ) : (
                                          <Minus className="w-4 h-4 text-theme-muted mx-auto" />
                                        )}
                                      </td>
                                      <td className="py-2 pr-4 text-right text-yellow-400 font-semibold">
                                        {r.score !== undefined ? r.score : '\u2014'}
                                      </td>
                                      <td className="py-2 text-right text-theme-muted text-xs">
                                        {elapsed !== null && elapsed >= 0
                                          ? elapsed < 60
                                            ? `${elapsed}s`
                                            : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`
                                          : '\u2014'}
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>

                          {/* Non-responders */}
                          {(() => {
                            const respondedIds = new Set(responses.map(r => r.player_id))
                            const missing = players.filter(p => !respondedIds.has(p.id))
                            if (missing.length === 0) return null
                            return (
                              <div className="mt-3 pt-3 border-t border-theme-border">
                                <p className="text-xs text-theme-muted mb-2">
                                  Did not respond ({missing.length}):
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {missing.map(p => (
                                    <span
                                      key={p.id}
                                      className="px-2 py-0.5 bg-surface text-theme-muted rounded text-xs"
                                    >
                                      {p.display_name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )
                          })()}
                        </div>
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}

function PlayersTab({
  players,
  activities,
  activityResponses,
  playerAnalyticsMap,
  expandedPlayerId,
  onTogglePlayer,
}: {
  players: Player[]
  activities: LessonActivity[]
  activityResponses: Record<string, RawResponse[]>
  playerAnalyticsMap: Record<string, DetailedPlayerAnalytics>
  expandedPlayerId: string | null
  onTogglePlayer: (id: string) => void
}) {
  // Build response lookup: playerId -> activityId -> response
  const playerActivityMap = useMemo(() => {
    const m: Record<string, Record<string, RawResponse>> = {}
    Object.entries(activityResponses).forEach(([actId, responses]) => {
      responses.forEach(r => {
        if (!m[r.player_id]) m[r.player_id] = {}
        m[r.player_id][actId] = r
      })
    })
    return m
  }, [activityResponses])

  // Compute effective score per player: analytics → serializer total_score → sum from responses
  const playerScoreMap = useMemo(() => {
    const m: Record<string, number> = {}
    players.forEach(p => {
      const analyticsScore = playerAnalyticsMap[p.id]?.totalScore
      if (analyticsScore !== undefined && analyticsScore !== null) {
        m[p.id] = analyticsScore
        return
      }
      if (p.total_score) {
        m[p.id] = p.total_score
        return
      }
      m[p.id] = Object.values(activityResponses)
        .flatMap(resps => resps.filter(r => String(r.player_id) === String(p.id)))
        .reduce((acc, r) => acc + Number(r.score ?? 0), 0)
    })
    return m
  }, [players, playerAnalyticsMap, activityResponses])

  // Sort players by total score descending
  const sortedPlayers = useMemo(
    () =>
      [...players].sort(
        (a, b) => (playerScoreMap[b.id] ?? 0) - (playerScoreMap[a.id] ?? 0),
      ),
    [players, playerScoreMap],
  )

  const maxScore = sortedPlayers.length > 0
    ? (playerScoreMap[sortedPlayers[0].id] ?? 0)
    : 0

  if (players.length === 0) {
    return (
      <div className="text-center py-16 text-theme-muted">
        <User className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p>No students in this lesson</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {sortedPlayers.map((player, rankIdx) => {
        const analytics = playerAnalyticsMap[player.id]
        const totalScore = playerScoreMap[player.id] ?? 0
        const sessionDuration = analytics?.sessionDuration ?? player.session_duration ?? 0
        const playerResponses = playerActivityMap[player.id] ?? {}
        const respondedCount = Object.keys(playerResponses).length
        const respondableCount = activities.filter(isRespondable).length
        const participation =
          respondableCount > 0
            ? Math.round((respondedCount / respondableCount) * 100)
            : 0
        const scorePct = maxScore > 0 ? (totalScore / maxScore) * 100 : 0
        const isExpanded = expandedPlayerId === player.id

        return (
          <div key={player.id} className="bg-surface-deep rounded-lg overflow-hidden">
            <button
              onClick={() => onTogglePlayer(player.id)}
              className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-surface-hover transition-colors"
            >
              {/* Rank */}
              <div className="shrink-0">{rankBadge(rankIdx + 1)}</div>

              {/* Avatar + name */}
              <div className="w-9 h-9 rounded-full bg-surface-hover flex items-center justify-center text-sm font-bold text-theme-text shrink-0">
                {(player.display_name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-theme-text truncate">{player.display_name}</div>
                <div className="text-xs text-theme-muted flex items-center gap-2 mt-0.5">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      player.connection_status === 'connected'
                        ? 'bg-green-400'
                        : 'bg-gray-500'
                    }`}
                  />
                  {player.is_registered ? 'Registered' : 'Guest'}
                </div>
              </div>

              {/* Stats row */}
              <div className="flex items-center gap-5 shrink-0 text-sm">
                {/* Score bar + value */}
                <div className="hidden md:flex items-center gap-2 w-32">
                  <div className="flex-1 h-2 bg-surface-hover rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all"
                      style={{ width: `${scorePct}%` }}
                    />
                  </div>
                  <span className="text-yellow-400 font-bold w-10 text-right">{totalScore}</span>
                </div>

                <div className="text-right hidden sm:block">
                  <div className="text-theme-text font-semibold">{respondedCount}/{respondableCount}</div>
                  <div className="text-theme-muted text-xs">responded ({participation}%)</div>
                </div>

                <div className="text-right hidden lg:block">
                  <div className="text-theme-text font-semibold">
                    {sessionDuration > 0 ? `${sessionDuration}m` : '\u2014'}
                  </div>
                  <div className="text-theme-muted text-xs">session</div>
                </div>

                <div className="text-yellow-400 font-bold md:hidden">{totalScore} pts</div>

                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-theme-muted" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-theme-muted" />
                )}
              </div>
            </button>

            {/* Per-activity drill-down */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4 border-t border-theme-border pt-4">
                    {/* Summary row */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {[
                        { label: 'Total Score', value: totalScore, color: 'text-yellow-400' },
                        {
                          label: 'Participation',
                          value: `${participation}%`,
                          color: 'text-blue-400',
                        },
                        {
                          label: 'Session',
                          value: sessionDuration > 0 ? `${sessionDuration}m` : '\u2014',
                          color: 'text-theme-text',
                        },
                      ].map(s => (
                        <div key={s.label} className="bg-surface rounded-lg p-3 text-center">
                          <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                          <div className="text-xs text-theme-muted mt-0.5">{s.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Per-activity table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-theme-muted border-b border-theme-border">
                            <th className="pb-2 w-6">#</th>
                            <th className="pb-2 pr-4">Activity</th>
                            <th className="pb-2 pr-4">Response</th>
                            <th className="pb-2 pr-4 text-center w-16">Result</th>
                            <th className="pb-2 text-right w-16">Score</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-theme-border">
                          {activities.map(activity => {
                            const response = playerResponses[activity.id]
                            const correct = response ? isCorrect(activity, response) : null
                            return (
                              <tr key={activity.id}>
                                <td className="py-2 pr-3 text-theme-muted text-xs font-mono">
                                  {activity.sequence_number}
                                </td>
                                <td className="py-2 pr-4">
                                  <div className="font-medium text-theme-text truncate max-w-[160px]">
                                    {activity.title}
                                  </div>
                                  <div className="text-xs text-theme-muted">
                                    {ACTIVITY_LABELS[activity.activity_type] ?? activity.activity_type}
                                  </div>
                                </td>
                                <td className="py-2 pr-4 text-theme-text-muted text-xs max-w-[180px] truncate">
                                  {response ? formatAnswer(activity, response) : (
                                    <span className="text-theme-muted italic">No response</span>
                                  )}
                                </td>
                                <td className="py-2 pr-4 text-center">
                                  {!response ? (
                                    <Minus className="w-4 h-4 text-theme-muted mx-auto" />
                                  ) : correct === true ? (
                                    <CheckCircle className="w-4 h-4 text-[var(--color-success)] mx-auto" />
                                  ) : correct === false ? (
                                    <XCircle className="w-4 h-4 text-[var(--color-danger)] mx-auto" />
                                  ) : (
                                    <Minus className="w-4 h-4 text-theme-muted mx-auto" />
                                  )}
                                </td>
                                <td className="py-2 text-right font-semibold text-yellow-400">
                                  {response?.score !== undefined ? response.score : (
                                    <span className="text-theme-muted">{'\u2014'}</span>
                                  )}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}

// Reports Tab

function ReportsTab({
  lesson,
  activities,
  activityResponses,
  players,
  playerAnalyticsMap,
}: {
  lesson: Lesson
  activities: LessonActivity[]
  activityResponses: Record<string, RawResponse[]>
  players: Player[]
  playerAnalyticsMap: Record<string, DetailedPlayerAnalytics>
}) {
  const [excelLoading, setExcelLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)

  const reportData: ReportData = {
    lesson: lesson,
    activities,
    activityResponses,
    players,
    playerAnalytics: playerAnalyticsMap,
  }

  const handleExcel = async () => {
    setExcelLoading(true)
    try {
      await generateExcelReport(reportData)
    } catch (e) {
    } finally {
      setExcelLoading(false)
    }
  }

  const handlePdf = async () => {
    setPdfLoading(true)
    try {
      await generatePdfReport(reportData, getCurrentPdfTheme())
    } catch (e) {
    } finally {
      setPdfLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4" data-tour="report-export">
        <button
          onClick={handleExcel}
          disabled={excelLoading}
          className="w-full flex items-center gap-4 p-6 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl hover:border-green-500/50 hover:bg-[var(--color-surface-hover)] transition-all text-left group disabled:opacity-60"
        >
          <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0">
            {excelLoading ? (
              <div className="animate-spin w-5 h-5 border-2 border-green-400 border-t-transparent rounded-full" />
            ) : (
              <FileSpreadsheet className="w-6 h-6 text-green-600 dark:text-green-400" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-[var(--color-text)] group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">Download Lesson Excel</div>
            <div className="text-sm text-[var(--color-text-muted)] mt-0.5">.xlsx with student scores, activity details, phishing breakdown</div>
          </div>
          <Download className="w-5 h-5 text-[var(--color-text-muted)] group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors shrink-0" />
        </button>
        <button
          onClick={handlePdf}
          disabled={pdfLoading}
          className="w-full flex items-center gap-4 p-6 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl hover:border-red-500/30 hover:bg-[var(--color-surface-hover)] transition-all text-left group disabled:opacity-60"
        >
          <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
            {pdfLoading ? (
              <div className="animate-spin w-5 h-5 border-2 border-red-400 border-t-transparent rounded-full" />
            ) : (
              <FileText className="w-6 h-6 text-red-600 dark:text-red-400" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-[var(--color-text)] group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">Download Lesson PDF</div>
            <div className="text-sm text-[var(--color-text-muted)] mt-0.5">Printable report with summary, per-activity tables, and phishing breakdown</div>
          </div>
          <Download className="w-5 h-5 text-[var(--color-text-muted)] group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors shrink-0" />
        </button>
      </div>

      {/* What's included */}
      <div className="bg-chrome-bg border border-theme-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-theme-text uppercase tracking-wide mb-3">What's Included</h3>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-theme-text-muted">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
            <span><strong className="text-theme-text">Summary</strong> -- lesson info, dates, response rate</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
            <span><strong className="text-theme-text">Student Performance</strong> -- scores & responses per activity</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
            <span><strong className="text-theme-text">Activity Details</strong> -- every response with correctness & time</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
            <span><strong className="text-theme-text">Phishing Breakdown</strong> -- per-category scores (if applicable)</span>
          </li>
        </ul>
      </div>
    </div>
  )
}

// Main Page

export function LessonReport() {
  return (
    <LessonReportErrorBoundary>
      <LessonReportContent />
    </LessonReportErrorBoundary>
  )
}

function LessonReportContent() {
  const { lessonId } = useParams<{ lessonId: string }>()
  const navigate = useNavigate()


  const [lesson, setLesson] = useState<Lesson | null>(null)
  const [playerAnalytics, setPlayerAnalytics] = useState<DetailedPlayerAnalytics[]>([])
  const [activityResponses, setActivityResponses] = useState<Record<string, RawResponse[]>>({})
  const [lessonOverview, setLessonOverview] = useState<LessonOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'leaderboard' | 'activities' | 'players' | 'reports'>('overview')
  const [expandedActivityId, setExpandedActivityId] = useState<string | null>(null)
  const [expandedPlayerId, setExpandedPlayerId] = useState<string | null>(null)

  useEffect(() => {
    if (!lessonId) return
    let cancelled = false

    const loadReport = async () => {
      try {
        setLoading(true)
        setError(null)

        let lessonRes: any
        try {
          lessonRes = await apiClient.getLesson(lessonId)
        } catch (apiErr: any) {
          const msg = apiErr?.response?.data?.error || apiErr?.message || 'Failed to load lesson'
          throw new Error(msg)
        }

        if (cancelled) return

        if (!lessonRes?.success) throw new Error(lessonRes?.error || 'Failed to load lesson')
        const lessonData: Lesson = lessonRes.data?.lesson ?? lessonRes.data
        if (!lessonData || !lessonData.id) throw new Error('Lesson data not available')
        setLesson(lessonData)

        const activities = Array.isArray(lessonData.activities) ? lessonData.activities : []

        // Fetch player analytics, lesson overview + all activity responses in parallel
        const [playersResult, overviewResult, ...responseResults] = await Promise.allSettled([
          apiClient.getLessonPlayersAnalytics(lessonId),
          apiClient.getLessonOverview(lessonId),
          ...activities.map(a =>
            apiClient.getResponses(lessonId, a.id),
          ),
        ])

        if (cancelled) return

        if (
          playersResult.status === 'fulfilled' &&
          (playersResult.value as any)?.success
        ) {
          setPlayerAnalytics((playersResult.value.data as any) ?? [])
        }

        if (overviewResult.status === 'fulfilled') {
          setLessonOverview((overviewResult.value as any)?.data ?? null)
        }

        const responseMap: Record<string, RawResponse[]> = {}
        activities.forEach((activity, idx) => {
          const result = responseResults[idx]
          if (result.status === 'fulfilled' && (result.value as any)?.success) {
            responseMap[activity.id] = ((result.value as any).data as RawResponse[]) ?? []
          } else {
            responseMap[activity.id] = []
          }
        })
        setActivityResponses(responseMap)
      } catch (err: any) {
        if (!cancelled) {
          console.error('[LessonReport] loadReport error:', err)
          setError(err.message || 'Failed to load report')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadReport()
    return () => { cancelled = true }
  }, [lessonId])

  // Player analytics lookup map
  const playerAnalyticsMap = useMemo(() => {
    const m: Record<string, DetailedPlayerAnalytics> = {}
    playerAnalytics.forEach(p => { m[p.id] = p })
    return m
  }, [playerAnalytics])

  // Leaderboard: players sorted by score, with fallback score computation from responses
  const leaderboard = useMemo((): LeaderboardEntry[] => {
    if (!lesson) return []
    return (lesson.players ?? [])
      .filter(p => !p.is_host)
      .map(p => {
        const analytics = playerAnalyticsMap[p.id]
        // Fallback: sum scores from raw responses if analytics not available
        const fallbackScore = Object.values(activityResponses)
          .flatMap(resps => resps.filter(r => r.player_id === p.id))
          .reduce((acc, r) => acc + Number(r.score ?? 0), 0)
        return {
          id: p.id,
          display_name: p.display_name,
          totalScore: analytics?.totalScore ?? fallbackScore,
          activitiesResponded: Object.values(activityResponses).filter(resps =>
            resps.some(r => r.player_id === p.id),
          ).length,
          sessionDuration: analytics?.sessionDuration ?? p.session_duration ?? 0,
        }
      })
      .sort((a, b) => b.totalScore - a.totalScore)
  }, [lesson, playerAnalyticsMap, activityResponses])

  // Summary stats
  const stats = useMemo(() => {
    if (!lesson) return null
    const activities = lesson.activities ?? []
    const respondableActivities = activities.filter(isRespondable)
    const nonHostPlayers = (lesson.players ?? []).filter(p => !p.is_host)
    const totalPlayers = nonHostPlayers.length
    const allResponses = Object.values(activityResponses).flat()
    const totalPossible = totalPlayers * respondableActivities.length
    const responseRate = totalPossible > 0 ? Math.round((allResponses.length / totalPossible) * 100) : 0
    const completedActivities = lesson.mode === 'self_paced'
      ? activities.filter(a => (activityResponses[a.id]?.length ?? 0) > 0).length
      : activities.filter(a => a.status === 'completed').length
    const avgScore =
      leaderboard.length > 0
        ? Math.round(leaderboard.reduce((acc, p) => acc + p.totalScore, 0) / leaderboard.length)
        : 0
    const topScore = leaderboard.length > 0 ? leaderboard[0].totalScore : 0
    return { totalPlayers, completedActivities, totalActivities: activities.length, responseRate, avgScore, topScore }
  }, [lesson, activityResponses, leaderboard])

  // Render

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg text-theme-text flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-theme-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-theme-text-muted">Loading report...</p>
        </div>
      </div>
    )
  }

  if (error || !lesson) {
    return (
      <div className="min-h-screen bg-theme-bg text-theme-text flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <p className="text-red-400 mb-4">{error ?? 'Lesson not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-surface-hover rounded-lg text-theme-text hover:bg-surface-hover transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  const activities = (lesson.activities ?? []).sort(
    (a, b) => a.sequence_number - b.sequence_number,
  )
  const nonHostPlayers = (lesson.players ?? []).filter(p => !p.is_host)

  return (
    <div className="min-h-screen bg-theme-bg text-theme-text">
      {/* Page header */}
      <div className="border-b border-theme-border bg-chrome-bg" data-tour="lesson-report-header">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg bg-surface-hover text-theme-text hover:bg-surface-hover transition-colors shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold truncate text-[var(--color-primary)]">{lesson.name}</h1>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase shrink-0 ${
                  lesson.status === 'completed' ? 'bg-green-500/10 text-green-500'
                  : lesson.status === 'live' || lesson.status === 'running' ? 'bg-blue-500/10 text-blue-500'
                  : 'bg-yellow-500/10 text-yellow-500'
                }`}>
                  {lesson.status}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-theme-text-muted flex-wrap">
                {lesson.ended_at && (
                  <span>
                    Ended:{' '}
                    {new Date(lesson.ended_at).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {formatDuration(lesson)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Summary stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            {[
              {
                icon: Users,
                label: 'Students',
                value: stats.totalPlayers,
                color: 'text-blue-400',
                bg: 'bg-blue-900/20',
              },
              {
                icon: BarChart2,
                label: 'Activities',
                value: `${stats.completedActivities}/${stats.totalActivities}`,
                color: 'text-purple-400',
                bg: 'bg-purple-900/20',
              },
              {
                icon: Trophy,
                label: 'Avg Score',
                value: stats.avgScore,
                color: 'text-yellow-400',
                bg: 'bg-yellow-900/20',
              },
              {
                icon: Award,
                label: 'Top Score',
                value: stats.topScore,
                color: 'text-green-400',
                bg: 'bg-green-900/20',
              },
              {
                icon: TrendingUp,
                label: 'Response Rate',
                value: `${stats.responseRate}%`,
                color: 'text-cyan-400',
                bg: 'bg-cyan-900/20',
              },
            ].map(({ icon: Icon, label, value, color, bg }) => (
              <div key={label} className={`${bg} border border-theme-border rounded-xl p-4 text-center`}>
                <Icon className={`w-5 h-5 mx-auto mb-2 ${color}`} />
                <div className={`text-2xl font-bold ${color}`}>{value}</div>
                <div className="text-xs text-theme-text-muted mt-1">{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-theme-border mb-6">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'leaderboard', label: `Leaderboard (${nonHostPlayers.length})` },
            { key: 'activities', label: `By Activity (${activities.length})` },
            { key: 'players', label: `By Student (${nonHostPlayers.length})` },
            { key: 'reports', label: 'Downloads' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              className={`px-5 py-2.5 font-medium border-b-2 transition-colors text-sm ${
                activeTab === tab.key
                  ? 'border-theme-primary text-theme-primary'
                  : 'border-transparent text-theme-text-muted hover:text-theme-text'
              }`}
              {...(tab.key === 'reports' ? { 'data-tour': 'report-export' } : {})}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
        >
          {activeTab === 'overview' && (
            <AnalyticsOverviewTab lessonOverview={lessonOverview} />
          )}
          {activeTab === 'leaderboard' && (
            <LeaderboardTab leaderboard={leaderboard} totalActivities={activities.filter(isRespondable).length} />
          )}
          {activeTab === 'activities' && (
            <ActivitiesTab
              activities={activities}
              activityResponses={activityResponses}
              players={nonHostPlayers}
              expandedActivityId={expandedActivityId}
              onToggleActivity={id =>
                setExpandedActivityId(prev => (prev === id ? null : id))
              }
            />
          )}
          {activeTab === 'players' && (
            <PlayersTab
              players={nonHostPlayers}
              activities={activities}
              activityResponses={activityResponses}
              playerAnalyticsMap={playerAnalyticsMap}
              expandedPlayerId={expandedPlayerId}
              onTogglePlayer={id =>
                setExpandedPlayerId(prev => (prev === id ? null : id))
              }
            />
          )}
          {activeTab === 'reports' && (
            <ReportsTab
              lesson={lesson}
              activities={activities}
              activityResponses={activityResponses}
              players={nonHostPlayers}
              playerAnalyticsMap={playerAnalyticsMap}
            />
          )}
        </motion.div>
      </div>
    </div>
  )
}
